<template>
  <div class="form-export-import">
    <v-dialog v-model="show" max-width="600px" persistent>
      <v-card>
        <v-card-title class="form-export-import__dialog-title">
          <v-icon small class="mr-2">
            {{ isImport ? "mdi-upload" : "mdi-download" }}
          </v-icon>
          {{ isImport ? "Formular importieren" : "Formular exportieren" }}
          <v-btn icon small class="ml-auto" @click="cancel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="py-6">
          <!-- Export-Modus -->
          <div v-if="!isImport">
            <p class="mb-4">Export als HTML-Widget:</p>

            <v-card flat outlined class="mb-4">
              <v-card-text class="form-export__preview">
                <div class="form-export-import__preview-title">
                  {{ exportFormatLabel }}
                </div>
                <code>{{ exportPreview }}</code>
              </v-card-text>
            </v-card>

            <v-card flat outlined class="mb-4">
              <v-card-text>
                <div class="form-export-import__theme-title">Widget-Farben</div>
                <div class="text-caption mb-2">
                  Standard: Mandanten-Farbpalette (falls gepflegt), sonst
                  Widget-Defaults.
                </div>

                <v-switch
                  v-model="useThemeOverrides"
                  dense
                  hide-details
                  class="mt-0"
                  label="Farben im Export überschreiben"
                ></v-switch>

                <div
                  v-if="useThemeOverrides"
                  class="form-export-import__theme-grid"
                >
                  <div
                    v-for="key in themeFieldKeys"
                    :key="`theme-${key}`"
                    class="form-export-import__theme-item"
                  >
                    <div class="form-export-import__theme-label">
                      {{ themeFieldLabels[key] }}
                    </div>
                    <div class="form-export-import__theme-inputs">
                      <input
                        v-model="widgetThemeOverrides[key]"
                        type="color"
                        class="form-export-import__theme-color-input"
                      />
                      <v-text-field
                        v-model="widgetThemeOverrides[key]"
                        dense
                        hide-details
                        outlined
                      ></v-text-field>
                    </div>
                  </div>
                </div>

                <div v-if="useThemeOverrides" class="d-flex justify-end mt-3">
                  <v-btn text small @click="resetThemeOverrides">
                    Zurücksetzen
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-btn block class="custom primary" @click="downloadExport">
              <v-icon small class="mr-1">mdi-download</v-icon>
              Herunterladen
            </v-btn>
          </div>

          <!-- Import-Modus -->
          <div v-else>
            <p class="mb-4">
              Lade eine JSON-Datei hoch, um ein Formular zu importieren:
            </p>

            <v-file-input
              v-model="importFile"
              accept=".json"
              label="JSON-Datei auswählen"
              outlined
              dense
              class="mb-4"
            ></v-file-input>

            <v-alert v-if="importPreview" type="info" outlined class="mb-4">
              <strong>Vorschau:</strong>
              <br />
              Formularname: {{ importPreview.name }}
              <br />
              Felder: {{ importPreview.fields?.length || 0 }}
            </v-alert>

            <v-alert v-if="importError" type="error" outlined class="mb-4">
              {{ importError }}
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="cancel">
            Abbrechen
          </v-btn>
          <v-btn
            v-if="!isImport"
            class="custom primary"
            :disabled="!form"
            @click="copyToClipboard"
          >
            In Zwischenablage kopieren
          </v-btn>
          <v-btn
            v-else
            class="custom primary"
            :disabled="!importFile || !importPreview"
            @click="confirmImport"
          >
            Importieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formHelper } from "@/helpers/formHelper.js";
import { getTokenHeader } from "@/helpers/commonHelper";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const FORMS_WIDGET_BASE_URL =
  env.forms_widget_base_url ||
  "https://eo-forms-widget-test.web11.oya-media.cloud/";
const WIDGET_SCRIPT_CLOSE_TAG = "</" + "script>";
const DEFAULT_WIDGET_THEME = {
  "color-accent": "#2468c9",
  "color-error": "#c55c52",
  "color-info": "#314d57",
  "color-primary": "#97bd0f",
  "color-secondary": "#5d7114",
  "color-text": "#333333",
  "color-warning": "#c55c52"
};
const THEME_FIELD_LABELS = {
  "color-primary": "Primärfarbe",
  "color-secondary": "Sekundärfarbe",
  "color-accent": "Akzentfarbe",
  "color-text": "Textfarbe",
  "color-info": "Infofarbe",
  "color-warning": "Warnfarbe",
  "color-error": "Fehlerfarbe"
};

export default {
  name: "FormExportImport",
  props: {
    form: {
      type: Object,
      default: null
    },
    isImport: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      show: true,
      importFile: null,
      importError: null,
      importPreview: null,
      useThemeOverrides: false,
      widgetThemeOverrides: { ...DEFAULT_WIDGET_THEME },
      persistedWidgetThemeOverrides: null,
      isSavingTheme: false,
      isHydratingThemeState: false,
      themeAutosaveTimer: null
    };
  },
  computed: {
    themeFieldKeys() {
      return Object.keys(THEME_FIELD_LABELS);
    },
    themeFieldLabels() {
      return THEME_FIELD_LABELS;
    },
    currentPrincipalId() {
      return this.$store.state.selectedPrincipal?.id || null;
    },
    currentFormId() {
      return this.form?._id || this.form?.id || null;
    },
    principalWidgetTheme() {
      const widgetColors = this.$store.state.selectedPrincipal?.colorPalette
        ?.forms?.widget?.colors;
      if (!Array.isArray(widgetColors)) return {};

      return widgetColors.reduce((acc, colorEntry) => {
        if (!colorEntry?.key || !colorEntry?.value) return acc;
        acc[colorEntry.key] = this.normalizeHexColor(colorEntry.value);
        return acc;
      }, {});
    },
    resolvedWidgetThemeDefaults() {
      return {
        ...DEFAULT_WIDGET_THEME,
        ...this.principalWidgetTheme
      };
    },
    normalizedWidgetThemeOverrides() {
      const overrides = {};
      this.themeFieldKeys.forEach(key => {
        const normalizedValue = this.normalizeHexColor(
          this.widgetThemeOverrides?.[key]
        );
        if (normalizedValue) {
          overrides[key] = normalizedValue;
        }
      });
      return overrides;
    },
    effectiveWidgetTheme() {
      if (!this.useThemeOverrides) {
        return this.resolvedWidgetThemeDefaults;
      }

      return {
        ...this.resolvedWidgetThemeDefaults,
        ...this.normalizedWidgetThemeOverrides
      };
    },
    canSaveThemeOverrides() {
      return (
        !!this.currentPrincipalId && !!this.currentFormId && !this.isSavingTheme
      );
    },
    exportFormatLabel() {
      return "Vorschau: HTML Widget";
    },
    exportPreview() {
      if (!this.form) return "";
      return this.getWidgetEmbedCode();
    }
  },
  watch: {
    form: {
      immediate: true,
      handler() {
        this.resetThemeState();
        this.loadThemeOverrides();
      }
    },
    useThemeOverrides(newValue, oldValue) {
      if (newValue === oldValue || this.isHydratingThemeState) return;
      this.scheduleThemeAutosave();
    },
    widgetThemeOverrides: {
      deep: true,
      handler() {
        if (this.isHydratingThemeState || !this.useThemeOverrides) return;
        this.scheduleThemeAutosave();
      }
    },
    importFile(newFile) {
      if (newFile) {
        this.readImportFile(newFile);
      }
    }
  },
  methods: {
    scheduleThemeAutosave() {
      if (this.themeAutosaveTimer) {
        clearTimeout(this.themeAutosaveTimer);
      }
      this.themeAutosaveTimer = setTimeout(() => {
        this.saveThemeOverrides({ silent: true });
      }, 400);
    },
    normalizeHexColor(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";

      const withHash = raw.startsWith("#") ? raw : `#${raw}`;
      const validHex = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(withHash);
      if (!validHex) return "";

      if (withHash.length === 4) {
        return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`.toLowerCase();
      }

      return withHash.toLowerCase();
    },
    resetThemeState() {
      this.useThemeOverrides = false;
      this.widgetThemeOverrides = { ...this.resolvedWidgetThemeDefaults };
      this.persistedWidgetThemeOverrides = null;
    },
    resetThemeOverrides() {
      // Reset bedeutet hier: nur Farbwerte auf Defaults zurücksetzen.
      this.widgetThemeOverrides = { ...this.resolvedWidgetThemeDefaults };
    },
    async loadThemeOverrides() {
      if (!this.currentPrincipalId || !this.currentFormId) return;

      try {
        this.isHydratingThemeState = true;
        const headers = getTokenHeader();
        const response = await this.$axios.get(
          `${env.form_builder_api_client_url}api/form-properties/${this.currentPrincipalId}/${this.currentFormId}`,
          { headers }
        );

        const savedProperties = response?.data?.data || {};
        const savedOverrides = savedProperties.widgetThemeOverrides;
        const savedEnabled = savedProperties.widgetThemeOverridesEnabled;

        if (typeof savedEnabled === "boolean") {
          this.useThemeOverrides = savedEnabled;
        }

        if (!savedOverrides || typeof savedOverrides !== "object") return;

        const normalized = {};
        this.themeFieldKeys.forEach(key => {
          const normalizedValue = this.normalizeHexColor(savedOverrides[key]);
          if (normalizedValue) normalized[key] = normalizedValue;
        });

        if (!Object.keys(normalized).length) return;

        this.persistedWidgetThemeOverrides = { ...normalized };
        this.widgetThemeOverrides = {
          ...this.resolvedWidgetThemeDefaults,
          ...normalized
        };
        if (typeof savedEnabled !== "boolean") {
          this.useThemeOverrides = true;
        }
      } catch (error) {
        // Keine gespeicherten Overrides ist ein valider Fall.
      } finally {
        this.isHydratingThemeState = false;
      }
    },
    async saveThemeOverrides(options = {}) {
      const { silent = false } = options;
      if (!this.canSaveThemeOverrides) return;

      try {
        this.isSavingTheme = true;
        const headers = getTokenHeader();

        let currentProperties = {};
        try {
          const response = await this.$axios.get(
            `${env.form_builder_api_client_url}api/form-properties/${this.currentPrincipalId}/${this.currentFormId}`,
            { headers }
          );
          currentProperties = response?.data?.data || {};
        } catch (e) {
          currentProperties = {};
        }

        const payload = {
          formId: this.currentFormId,
          principal_id: this.currentPrincipalId,
          recipients: Array.isArray(currentProperties.recipients)
            ? currentProperties.recipients
            : [],
          recipientsFreetext: currentProperties.recipientsFreetext || "",
          cc: Array.isArray(currentProperties.cc) ? currentProperties.cc : [],
          ccFreetext: currentProperties.ccFreetext || "",
          submissionReaderGroupId:
            currentProperties.submissionReaderGroupId || null,
          replyToEmail: currentProperties.replyToEmail || "",
          senderName: currentProperties.senderName || "",
          nameFieldId: currentProperties.nameFieldId || null,
          ccEmailFieldId: currentProperties.ccEmailFieldId || null,
          successMessage: currentProperties.successMessage || "",
          redirectUrl: currentProperties.redirectUrl || "",
          widgetThemeOverridesEnabled: this.useThemeOverrides,
          // Widget liest Farben aus den Form-Properties, daher immer die
          // aktuell wirksamen Farben persistieren.
          widgetThemeOverrides: this.getWidgetThemeForPersistence()
        };

        await this.$axios.put(
          `${env.form_builder_api_client_url}api/form-properties/${this.currentPrincipalId}/${this.currentFormId}`,
          payload,
          { headers }
        );

        if (this.useThemeOverrides) {
          this.persistedWidgetThemeOverrides = {
            ...this.normalizedWidgetThemeOverrides
          };
          if (!silent) {
            this.$root.$snackbar("Formular-Farbüberschreibung gespeichert");
          }
        } else {
          this.persistedWidgetThemeOverrides = null;
          if (!silent) {
            this.$root.$snackbar("Mandanten-Defaultfarben aktiv");
          }
        }
      } catch (error) {
        console.error("Fehler beim Speichern der Formularfarben:", error);
        this.$root.$snackbar(
          "Fehler beim Speichern der Formularfarben",
          "error"
        );
      } finally {
        this.isSavingTheme = false;
      }
    },
    getWidgetThemeForPersistence() {
      if (!this.useThemeOverrides) {
        return { ...this.resolvedWidgetThemeDefaults };
      }

      return {
        ...this.resolvedWidgetThemeDefaults,
        ...this.normalizedWidgetThemeOverrides
      };
    },
    getWidgetEmbedCode() {
      const rawApiUrl = env.form_builder_api_client_url || "";
      const apiUrl = rawApiUrl.endsWith("/")
        ? rawApiUrl.slice(0, -1)
        : rawApiUrl;
      const formId = this.form?._id || this.form?.id || "";

      return `<div id="eoforms-widget" class="eoforms-widget"
     data-api-url="${apiUrl}"
     data-font="Mukta, sans-serif"
     data-form-id="${formId}"
>
</div>

<div class="eoforms-async-scripts" data-path="${FORMS_WIDGET_BASE_URL}">
    <div data-name="eoforms-formsWidget"></div>
</div>
<script src="${FORMS_WIDGET_BASE_URL}loadAsyncForms.js" type="module">${WIDGET_SCRIPT_CLOSE_TAG}
<link href="${FORMS_WIDGET_BASE_URL}eoforms-Variables.css" rel="stylesheet" type="text/css">`;
    },
    downloadExport() {
      const data = this.getWidgetEmbedCode();
      const filename = `${this.form.name || "form"}.html`;
      const mimeType = "text/html";
      const blob = new Blob([data], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      this.$emit("exported");
    },
    copyToClipboard() {
      const text = this.exportPreview;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$root.$snackbar("In Zwischenablage kopiert!");
        })
        .catch(err => {
          console.error("Fehler beim Kopieren:", err);
        });
    },
    readImportFile(file) {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          this.importError = null;
          const content = e.target.result;
          const importedForm = formHelper.importFormFromJson(content);

          if (!importedForm || !importedForm.fields) {
            this.importError =
              "Fehler beim Lesen der Datei: Ungültiges Formularformat";
            this.importPreview = null;
            return;
          }

          this.importPreview = importedForm;
        } catch (error) {
          this.importError = `Fehler beim Lesen der Datei: ${error.message}`;
          this.importPreview = null;
        }
      };
      reader.readAsText(file);
    },
    confirmImport() {
      this.$emit("imported", this.importPreview);
      this.cancel();
    },
    cancel() {
      this.$emit("cancel");
      this.show = false;
      if (this.themeAutosaveTimer) {
        clearTimeout(this.themeAutosaveTimer);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.form-export-import {
  &__dialog-title {
    display: flex !important;
    flex-direction: row !important;
    justify-content: flex-start !important;
    align-items: center !important;
    text-align: left !important;
    padding: 16px 24px !important;
    height: auto !important;
    gap: 8px !important;
  }

  &__preview-title {
    font-size: 12px;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
  }

  .form-export__preview {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 12px;
    max-height: 300px;
    overflow-y: auto;

    code {
      font-size: 12px;
      line-height: 1.4;
      word-break: break-all;
      white-space: pre-wrap;
    }
  }

  &__theme-title {
    font-size: 13px;
    font-weight: 600;
  }

  &__theme-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
    margin-top: 10px;
  }

  &__theme-item {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 8px;
    background: #fcfcfc;
  }

  &__theme-label {
    font-size: 12px;
    color: #555;
    margin-bottom: 6px;
    font-weight: 600;
  }

  &__theme-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__theme-color-input {
    width: 34px;
    min-width: 34px;
    height: 34px;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    padding: 0;
    background: #fff;
  }
}
</style>
